import React from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { useLanguage } from '../i18n';

/**
 * Multi-step fluid processing animation:
 * Uploading -> Skeletal tracking -> AI analyzing -> Generating report
 */
export const ProcessingLoader = ({ currentStep, uploadProgress }) => {
  const { t } = useLanguage();
  const steps = [
    { id: 'uploading', label: t('stepUploading') },
    { id: 'tracking', label: t('stepTracking') },
    { id: 'analyzing', label: t('stepAnalyzing') },
    { id: 'generating', label: t('stepGenerating') },
  ];
  const currentIdx = steps.findIndex((s) => s.id === currentStep);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="ss-card p-6 overflow-hidden relative"
      data-testid="processing-loader"
    >
      <div className="ss-scanline" />
      <div className="flex justify-center gap-2 py-6">
        <div className="ss-dot" />
        <div className="ss-dot" />
        <div className="ss-dot" />
      </div>

      <div className="space-y-4 relative z-10">
        {steps.map((step, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <div key={step.id} className="flex items-center gap-3" data-testid={`processing-step-${step.id}`}>
              <span
                className={`w-7 h-7 rounded-[12px] flex items-center justify-center border text-xs font-mono transition-colors ${
                  done
                    ? 'bg-[rgba(34,197,94,0.12)] border-[rgba(34,197,94,0.35)] text-[#22C55E]'
                    : active
                    ? 'bg-white/[0.1] border-white/20 text-white'
                    : 'bg-white/[0.03] border-white/10 text-white/40'
                }`}
              >
                {done ? <Check size={13} /> : active ? <Loader2 size={13} className="animate-spin" /> : i + 1}
              </span>
              <div className="flex-1">
                <span className={`text-sm ${active ? 'text-white font-medium' : done ? 'text-white/45 line-through decoration-white/20' : 'text-white/45'}`}>
                  {step.label}
                </span>
                {active && step.id === 'uploading' && uploadProgress != null && (
                  <div className="mt-1.5 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full bg-[#22C55E] transition-[width] duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                )}
              </div>
              {active && step.id === 'uploading' && uploadProgress != null && (
                <span className="font-mono text-xs text-white/70">{uploadProgress}%</span>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-white/45 font-mono text-center mt-6">{t('processingHint')}</p>
    </motion.div>
  );
};
